import { ArrowBack } from '@mui/icons-material';
import { Alert, Box, Button, FormControlLabel, Switch, TextField, Typography } from '@mui/material';
import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import apiReq from 'core/api';
import { Role, User } from 'types';
import { useApiFormErrorHandler } from 'hooks/useApiFormErrorHandler';
import { validateEmail } from 'modules/auth';
import { USE_NEW_SHELL } from 'core/flags';
import { FormPageV2, Button as ShellButton } from 'components/AppShell';
import RoleSelect from './RoleSelect';
import QueuesSelect from './QueuesSelect';
import { useSnackbar } from 'components/GlobalSnackbar';
import { YandexTrackerApi } from 'api/yandex-tracker';
import type { YandexTrackerQueue } from 'model/yandex-tracker';

interface FormErrors {
  name?: string;
  email?: string;
  role?: string;
  [key: string]: string | undefined;
}

const UserForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [roles, setRoles] = useState<Role[]>([]);
  const [queues, setQueues] = useState<YandexTrackerQueue[]>([]);
  const [apiError, setApiError] = useState<string>('');
  const handleApiError = useApiFormErrorHandler();

  const [formData, setFormData] = useState<User>({
    name: '',
    email: '',
    roles: [],
    is_active: true,
    is_admin: false,
    yandex_tracker_queues: [],
  });

  useEffect(() => {
    apiReq<Role[]>('roles/all').then((roles) => {
      setRoles(roles || []);
    });
  }, []);

  useEffect(() => {
    YandexTrackerApi.getQueues()
      .then((list) => setQueues(list ?? []))
      .catch(() => setQueues([]));
  }, []);

  const [errors, setErrors] = useState<FormErrors>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const showSnackbar = useSnackbar();

  const loadUser = useCallback(async () => {
    setApiError('');
    try {
      setIsLoading(true);
      const user = await apiReq<User>(`user/${id}`);
      if (user) {
        setFormData({
          ...user,
          yandex_tracker_queues: Array.isArray(user.yandex_tracker_queues)
            ? user.yandex_tracker_queues.map((el) => el.id)
            : [],
        });
      }
    } catch (error) {
      setApiError('Ошибка загрузки пользователя');
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (isEdit) {
      loadUser();
    }
  }, [isEdit, loadUser]);

  const validateForm = () => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Имя обязательно';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email обязателен';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Некорректный email';
    }

    // Проверяем, что roles существует и не пустой массив
    if (!formData.roles || !Array.isArray(formData.roles) || formData.roles.length === 0) {
      newErrors.role = 'Роль обязательна';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setApiError('');

    if (!validateForm()) {
      return;
    }

    try {
      setIsSubmitting(true);
      let response;

      const rolesIdList = formData.roles.map((role) => role.id);
      const userData = {
        ...formData,
        roles: rolesIdList,
        yandex_tracker_queues: formData.yandex_tracker_queues ?? [],
      };

      if (isEdit) {
        response = await apiReq<User>(`user/update`, 'POST', { ...userData, id: id });
      } else {
        response = await apiReq<User>(`user/create`, 'POST', userData);
      }

      if (response) {
        showSnackbar({ message: 'Пользователь успешно сохранен', severity: 'success' });
        navigate('/users');
      } else {
        setApiError('Ошибка сохранения пользователя');
      }
    } catch (error) {
      handleApiError(error, setErrors, setApiError);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: keyof User) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setFormData((prev) => ({ ...prev, [field]: value }));

    // Очищаем ошибку при изменении поля
    if (errors[field as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const handleStatusChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const isActive = event.target.checked;
    setFormData((prev) => ({ ...prev, is_active: isActive }));
  };

  const handleRolesChange = (selectedRoles: Role[]) => {
    setFormData((prev) => ({ ...prev, roles: selectedRoles }));

    if (errors.role) {
      setErrors((prev) => ({ ...prev, role: '' }));
    }
  };

  const handleQueuesChange = (queueKeys: string[]) => {
    setFormData((prev) => ({ ...prev, yandex_tracker_queues: queueKeys }));
  };

  const fields = (
    <Box component="form" id="user-form" onSubmit={handleSubmit}>
      <TextField
        fullWidth
        label="Имя"
        value={formData.name}
        onChange={handleInputChange('name')}
        error={Boolean(errors.name)}
        helperText={errors.name}
        margin="normal"
        required
      />
      <TextField
        fullWidth
        label="Email"
        type="email"
        value={formData.email}
        onChange={handleInputChange('email')}
        error={Boolean(errors.email)}
        helperText={errors.email}
        margin="normal"
        required
      />
      <RoleSelect
        value={formData.roles}
        onChange={handleRolesChange}
        options={roles}
        error={Boolean(errors.role)}
        helperText={errors.role}
      />
      <QueuesSelect
        value={Array.isArray(formData.yandex_tracker_queues) ? formData.yandex_tracker_queues : []}
        onChange={handleQueuesChange}
        options={queues}
      />
      {errors.roles && <Typography color="error">{errors.roles}</Typography>}
      <FormControlLabel
        control={<Switch checked={formData.is_active} onChange={handleStatusChange} />}
        label="Активен"
        sx={{ mt: 2 }}
      />
      <FormControlLabel
        control={<Switch checked={formData.is_admin} onChange={handleInputChange('is_admin')} />}
        label="Администратор"
        sx={{ mt: 2 }}
      />
      {!USE_NEW_SHELL && (
        <Box sx={{ mt: 4, display: 'flex', gap: 2 }}>
          <Button type="submit" variant="contained" disabled={isSubmitting} sx={{ minWidth: 120 }}>
            {isSubmitting ? 'Сохранение...' : 'Сохранить'}
          </Button>
          <Button variant="outlined" onClick={() => navigate('/users')} disabled={isSubmitting}>
            Отмена
          </Button>
        </Box>
      )}
    </Box>
  );

  if (USE_NEW_SHELL) {
    return (
      <FormPageV2
        title={isEdit ? 'Редактировать пользователя' : 'Создать пользователя'}
        backTo="/users"
        isLoading={isLoading}
        errorBanner={apiError ? <Alert severity="error">{apiError}</Alert> : undefined}
        footer={
          <>
            <ShellButton variant="ghost" onClick={() => navigate('/users')} disabled={isSubmitting}>
              Отмена
            </ShellButton>
            <ShellButton variant="primary" type="submit" form="user-form" disabled={isSubmitting}>
              {isSubmitting ? 'Сохранение…' : 'Сохранить'}
            </ShellButton>
          </>
        }
      >
        {fields}
      </FormPageV2>
    );
  }

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>Загрузка...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 600, mx: 'auto' }}>
      <Box display="flex" alignItems="center" mb={3}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/users')} sx={{ mr: 2 }}>
          Назад
        </Button>
        <Typography variant="h4" component="h1">
          {isEdit ? 'Редактировать пользователя' : 'Создать пользователя'}
        </Typography>
      </Box>
      {apiError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {apiError}
        </Alert>
      )}
      {fields}
    </Box>
  );
};

export default UserForm;
